import {createContext} from 'preact';
import type {Dispatch, StateUpdater} from 'preact/hooks';
import {useContext} from 'preact/hooks';

import type {Cart} from '~/types/cart';

import type {
  AvailableLoanType,
  InstallmentPlan,
  InstallmentsBannerContent,
  InstallmentsBannerType,
  InstallmentsModalVariant,
  MinIneligibleMessageType,
  SamplePlan,
  Term,
  VariantModalDetails,
} from '../types';

export interface InstallmentsContextType {
  backgroundColor: string;
  bannerContent: InstallmentsBannerContent;
  canShowSamplePlanModalContent: boolean;
  cartDetails?: Cart;
  checkoutUrl: string | null;
  countryCode?: string;
  currencyCode?: string;
  dataLoaded: boolean;
  eligible: boolean;
  financingTermForBanner: Term | null | undefined;
  fullPrice: string;
  hasZeroInterestLoanType: boolean;
  installmentPlans: InstallmentPlan[];
  installmentsBuyerPrequalificationEnabled: boolean;
  isEligibleForPrequalification: boolean;
  isInAdaptiveRangeWithoutZeroInterest: boolean;
  isLegacyBanner: boolean;
  loanTypes: AvailableLoanType[];
  maxPrice?: string;
  metaType: InstallmentsBannerType;
  minIneligibleMessageType?: MinIneligibleMessageType;
  minPrice: string;
  isModalOpen: boolean;
  modalToken: string;
  modalVariant: InstallmentsModalVariant;
  numberOfPaymentTerms: number;
  pricePerTerm: string;
  samplePlans: SamplePlan[];
  sellerId?: string;
  setIsModalOpen: Dispatch<StateUpdater<boolean>>;
  showNewBuyerIncentive: boolean;
  showPrequalAmount: boolean;
  prequalAmountLoaded: boolean;
  setShowPrequalAmount: Dispatch<StateUpdater<boolean>>;
  setPrequalAmountLoaded: Dispatch<StateUpdater<boolean>>;
  uxMode?: 'iframe' | 'windoid';
  variantAvailable: boolean;
  variantId?: number;
  variantInfo?: VariantModalDetails;
}

export const initialContext: InstallmentsContextType = {
  backgroundColor: '#fff',
  bannerContent: 'pay_in_4',
  canShowSamplePlanModalContent: false,
  cartDetails: undefined,
  checkoutUrl: null,
  countryCode: 'US',
  currencyCode: 'USD',
  dataLoaded: false,
  eligible: false,
  financingTermForBanner: null,
  fullPrice: '',
  hasZeroInterestLoanType: false,
  installmentPlans: [],
  installmentsBuyerPrequalificationEnabled: false,
  isEligibleForPrequalification: false,
  isInAdaptiveRangeWithoutZeroInterest: false,
  isLegacyBanner: false,
  loanTypes: [],
  maxPrice: '$3000',
  metaType: 'product',
  minIneligibleMessageType: undefined,
  minPrice: '$50',
  isModalOpen: false,
  modalToken: '',
  modalVariant: 'splitPay',
  numberOfPaymentTerms: 4,
  pricePerTerm: '',
  samplePlans: [],
  sellerId: undefined,
  showNewBuyerIncentive: false,
  showPrequalAmount: false,
  prequalAmountLoaded: false,
  setShowPrequalAmount: () => {
    throw new Error(
      'Invalid attempt to call setShowPrequalAmount from outside of context',
    );
  },
  setPrequalAmountLoaded: () => {
    throw new Error(
      'Invalid attempt to call setPrequalAmountLoaded from outside of context',
    );
  },
  setIsModalOpen: () => {
    throw new Error(
      'Invalid attempt to call setIsModalOpen from outside of context',
    );
  },
  uxMode: 'iframe',
  variantAvailable: false,
  variantId: undefined,
  variantInfo: undefined,
};

export const InstallmentsContext =
  createContext<InstallmentsContextType>(initialContext);

export const useInstallments = () => {
  return useContext(InstallmentsContext);
};
